// Pure intake behavior: tool-row exclusivity, the breakdown check, footer labels.
import { breakdownSum, REVIEW_STEP, toolLabel, type IntakeAnswers, type ToolRow, type ToolId } from '@upm/domain';

// ---------------------------------------------------------------- tool rows

/** Picking a listed tool clears Not applicable. */
export function toggleTool(row: ToolRow, tool: ToolId): ToolRow {
  const tools = row.tools.includes(tool) ? row.tools.filter((t) => t !== tool) : [...row.tools, tool];
  return { ...row, tools, notApplicable: false };
}

/** Other: '' while selected but untyped, null when not selected. Clears Not applicable. */
export function toggleOther(row: ToolRow): ToolRow {
  return { ...row, other: row.other === null ? '' : null, notApplicable: false };
}

/** Not applicable is exclusive: selecting it clears tools and Other. */
export function toggleNotApplicable(row: ToolRow): ToolRow {
  return row.notApplicable
    ? { ...row, notApplicable: false }
    : { ...row, tools: [], other: null, notApplicable: true };
}

export type RowChip = { key: string; label: string; na?: boolean };
export function rowChips(row: ToolRow, labels: Record<string, string>): RowChip[] {
  if (row.notApplicable) return [{ key: 'na', label: 'Not applicable', na: true }];
  const chips: RowChip[] = row.tools.map((t) => ({ key: t, label: toolLabel(t, labels) }));
  if (row.other !== null) chips.push({ key: 'other', label: row.other.trim() ? `Other: ${row.other.trim()}` : 'Other' });
  return chips;
}

/** Plain text of a row's answer for the review step ("" when unanswered). */
export function rowText(row: ToolRow | undefined, labels: Record<string, string>): string {
  if (!row) return '';
  if (row.notApplicable) return 'Not applicable';
  const parts = row.tools.map((t) => toolLabel(t, labels));
  if (row.other !== null) parts.push(row.other.trim() || 'Other');
  return parts.join(', ');
}

/** Multi-choice toggle where one value (e.g. docsScope 'none') is exclusive. */
export function toggleMulti<T extends string>(list: readonly T[] | undefined, value: T, exclusive?: T): T[] {
  const cur = [...(list ?? [])];
  if (exclusive !== undefined && value === exclusive) return cur.includes(value) ? [] : [value];
  const rest = exclusive !== undefined ? cur.filter((x) => x !== exclusive) : cur;
  return rest.includes(value) ? rest.filter((x) => x !== value) : [...rest, value];
}

// ---------------------------------------------------------------- breakdown check

export type BreakdownCheck = { text: string; tone: 'ok' | 'warn' };

/** Live check under the client breakdown. Null until any breakdown number is entered. */
export function breakdownCheck(a: Pick<IntakeAnswers, 'clientsTotal' | 'breakdown'>): BreakdownCheck | null {
  const sum = breakdownSum(a as IntakeAnswers);
  if (sum === null) return null;
  const total = a.clientsTotal;
  if (typeof total !== 'number' || sum === total) return { text: `Adds up to ${sum} clients.`, tone: 'ok' };
  return { text: `These add up to ${sum}, and you said about ${total}. Rough numbers are fine.`, tone: 'warn' };
}

// ---------------------------------------------------------------- navigation

export const STEP_TITLES = ['Welcome', 'Your firm', 'Your tools', 'Clients and work', 'Documents', 'Your questions', 'Review and submit', 'Book your Discovery Session'] as const;

/** Last questionnaire step (Review and submit, 6); then booking (7) and the confirmation (8). */
export { REVIEW_STEP };
export const BOOKING_STEP = REVIEW_STEP + 1;
export const DONE_STEP = BOOKING_STEP + 1;

/** The primary footer button for a step. `submitted` = editing after submit. */
export function nextLabel(step: number, submitted = false): string {
  if (step === 0) return 'Get started';
  if (step === REVIEW_STEP) return submitted ? 'Back to your migration status' : 'Submit and book your call';
  return 'Continue';
}

/** Stepper progress: the questionnaire steps plus booking count, done = before the current one. */
export function progress(step: number): { pct: number; label: string } {
  const total = BOOKING_STEP;
  const done = Math.min(Math.max(step - 1, 0), total);
  if (step >= DONE_STEP) return { pct: 100, label: 'Complete' };
  return { pct: Math.round((done / total) * 100), label: step === 0 ? 'Not started' : `${done} of ${total} steps done` };
}
